import type {
  Catalog,
  GraphNode,
  NodeData,
  NodeType,
  RunStatus,
  StepStatus
} from '@/lib/workflows'
import { WEEKDAYS } from '@/lib/workflows'
import {
  Bot,
  Braces,
  Globe,
  Hourglass,
  type LucideIcon,
  MessageSquareText,
  Repeat,
  ScanSearch,
  ShieldCheck,
  Split,
  Wrench,
  Zap
} from 'lucide-react'
import { createContext, useContext } from 'react'

export const CatalogContext = createContext<Catalog | null>(null)
export const useCatalog = () => useContext(CatalogContext)

/** Step names by id, so `{{n2.email}}` can read as ‹Find the customer: email›. */
export const LabelsContext = createContext<Record<string, string>>({})
export const useLabels = () => useContext(LabelsContext)

const TEMPLATE = /\{\{\s*([\w-]+)((?:\.[\w-]+)*)\s*\}\}/g

/** Template codes in plain words, for showing on the canvas (the stored text is unchanged). */
export function friendly(text: string, labels: Record<string, string>) {
  return text.replace(TEMPLATE, (_, id: string, path: string) => {
    const name =
      id === 'trigger'
        ? 'what started it'
        : id === 'previous'
          ? 'the step before'
          : id === 'error'
            ? 'the error'
            : (labels[id] ?? id)
    const field = path ? path.slice(1).replace(/\./g, ' › ').replace(/_/g, ' ') : ''
    return `‹${name}${field && field !== 'text' ? `: ${field}` : ''}›`
  })
}

export const TYPE_META: Record<NodeType, { label: string; icon: LucideIcon }> = {
  trigger: { label: 'Trigger', icon: Zap },
  agent: { label: 'Agent', icon: Bot },
  action: { label: 'Action', icon: Wrench },
  condition: { label: 'Check', icon: Split },
  approval: { label: 'Your approval', icon: ShieldCheck },
  wait: { label: 'Wait', icon: Hourglass },
  notify: { label: 'Message', icon: MessageSquareText },
  http: { label: 'Web call', icon: Globe },
  set: { label: 'Record', icon: Braces },
  loop: { label: 'For each', icon: Repeat },
  classify: { label: 'Smart check', icon: ScanSearch }
}

export const BRANCHING: NodeType[] = ['condition', 'approval', 'classify']

const EVERY_LABEL: Record<string, string> = {
  '15m': 'Every 15 minutes',
  hourly: 'Every hour',
  daily: 'Every day',
  weekly: 'Every week'
}

function clock(value?: string) {
  if (!value || !/^\d{1,2}:\d{2}$/.test(value)) return ''
  const [h, m] = value.split(':').map(Number)
  const suffix = h >= 12 ? 'PM' : 'AM'
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${suffix}`
}

export function describeTrigger(data: NodeData, catalog: Catalog | null) {
  if (data.kind === 'event') {
    const label = catalog?.events.find((e) => e.key === data.event)?.label
    return label
      ? `When ${label.charAt(0).toLowerCase()}${label.slice(1)}`
      : 'When something happens'
  }
  if (data.kind === 'schedule') {
    const every = data.every ?? 'daily'
    let text = EVERY_LABEL[every] ?? 'Every day'
    if (every === 'weekly' && typeof data.weekday === 'number')
      text += ` on ${WEEKDAYS[data.weekday]}`
    const at = clock(data.at_time)
    if ((every === 'daily' || every === 'weekly') && at) text += ` at ${at}`
    return text
  }
  if (data.kind === 'webhook') return 'When another app calls its web address'
  return 'When you press Run'
}

export function agentName(key: string | undefined, catalog: Catalog | null) {
  return catalog?.agents.find((a) => a.key === key)?.name ?? key ?? ''
}

/** One line under a node's title saying what it does. */
export function nodeSummary(node: GraphNode, catalog: Catalog | null) {
  const d = node.data
  switch (node.type) {
    case 'trigger':
      return describeTrigger(d, catalog)
    case 'agent':
      return d.instructions || 'Tell the agent what to do'
    case 'action': {
      const action = catalog?.actions.find((a) => a.name === d.tool)
      return action ? `${action.label} · asks you first` : 'Pick an action'
    }
    case 'condition': {
      if (d.mode === 'ai') return d.question || 'Write the yes/no question'
      const op = catalog?.ops.find((o) => o.key === d.op)?.label ?? d.op ?? ''
      const empty = d.op === 'is_empty' || d.op === 'not_empty'
      return `${d.value || '{{previous}}'} ${op}${empty ? '' : ` ${d.compare || '…'}`}`
    }
    case 'approval':
      return d.title || 'You approve before it goes on'
    case 'wait':
      return d.amount ? `${d.amount} ${d.unit ?? 'hours'}` : 'Set how long'
    case 'notify':
      return (d.message || 'Write the message') + (d.email_owner ? ' · emails you' : '')
    case 'http': {
      const host = (d.url ?? '').replace(/^https?:\/\//, '').split('/')[0]
      return host ? `${d.method ?? 'GET'} ${host}` : 'Set the web address'
    }
    case 'set': {
      const names = Object.keys(d.fields ?? {})
      return names.length ? names.join(', ') : 'Add fields'
    }
    case 'classify':
      return d.target
        ? `${d.source === 'items' ? 'In the list' : 'New posts'}: ${d.target}`
        : 'Say what counts as a match'
    case 'loop':
      return d.instructions
        ? `${d.mode === 'filter' ? 'Keep if' : 'Each'}: ${d.instructions}`
        : 'Say what to do with each item'
  }
}

export function nodeTitle(node: GraphNode) {
  return node.data.label || TYPE_META[node.type].label
}

export const RUN_TONE: Record<RunStatus, string> = {
  running: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
  waiting: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
  done: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  failed: 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400',
  stopped: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
}

export const STEP_RING: Record<StepStatus, string> = {
  running: 'ring-2 ring-amber-500',
  waiting: 'ring-2 ring-amber-500',
  resumed: 'ring-2 ring-amber-500',
  done: 'ring-2 ring-emerald-500',
  failed: 'ring-2 ring-red-500',
  skipped: 'opacity-50'
}

export function timeAgo(iso: string | null) {
  if (!iso) return 'never'
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  })
}

export const panel =
  'rounded-[36px] border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-black'

export const field =
  'w-full rounded-[24px] border border-neutral-200 bg-white px-3.5 py-2 text-sm text-neutral-900 outline-none transition-colors placeholder:text-neutral-400 focus:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white dark:focus:border-neutral-600'

export const fieldLabel =
  'mb-1.5 block text-xs font-semibold text-neutral-500 dark:text-neutral-400'
